import { Loading } from "../../app/Status";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import {
  METER_DEFAULT_UNITS,
  METER_TYPE_LABELS,
  METER_TYPES,
  METER_UNIT_SUGGESTIONS,
  READING_EVENT_LABELS,
  REGISTER_DIRECTION_LABELS,
  REGISTER_DIRECTIONS,
  SIGNAL_LABELS,
  todayIso,
  type AssetDto,
  type AssetMeterDto,
  type MeterDto,
  type MeterType,
  type RegisterDirection,
} from "@klantpunt/shared";
import { IconAlert, IconPlus, IconTrash } from "../../app/icons";
import { Modal } from "../../app/Modal";
import { api, ApiError } from "../../lib/api";
import { hasModule, useMe } from "../../lib/auth";
import { photoUrl, thumbUrl } from "../../lib/photos";
import { useToast } from "../../lib/toast";
import { MeterChart } from "./MeterChart";

/**
 * Blok *Meter* bij een asset van soort Meter (docs/METERSTANDEN.md §3 en §7): instellingen en
 * telwerken, leegstand, signalen, verbruik per periode met een jaar eerder, en de opnames.
 * Alleen met recht op de module Meterstanden.
 */
export const assetMeterKey = (id: string) => ["asset-meter", id] as const;
const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : "Er ging iets mis");
export const fmtDate = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" });
export const nl = (n: number | null | undefined, digits = 3) =>
  n == null ? "—" : n.toLocaleString("nl-NL", { maximumFractionDigits: digits });
/** Getal uit een veld: komma of punt als decimaalteken, punten als duizendtal bij een komma. */
export function parseNumber(s: string): number | null {
  const t = s.trim().replace(/\s/g, "");
  if (!t) return null;
  const n = Number(/,/.test(t) ? t.replace(/\./g, "").replace(",", ".") : t);
  return Number.isFinite(n) ? n : null;
}

/** Meter, opnames en signalen van één asset (gedeeld met het aandachtblok in het assetpaneel). */
export function useAssetMeter(assetId: string, enabled: boolean) {
  return useQuery({
    queryKey: assetMeterKey(assetId),
    queryFn: () => api<AssetMeterDto>(`/assets/${assetId}/meter`),
    enabled,
  });
}

/** Naar de open meterronde van het gebouw, met deze meter voorgeselecteerd. */
export async function openMeterRound(assetId: string, navigate: (to: string) => void, toast: (msg: string) => void) {
  try {
    const r = await api<{ roundId: string | null; completedRoundId: string | null }>(`/assets/${assetId}/meter/round`);
    if (r.roundId) return navigate(`/meterstanden/ronde/${r.roundId}?meter=${assetId}`);
    if (r.completedRoundId) {
      toast("De ronde van deze periode is afgerond. Heropen hem om deze meter alsnog op te nemen.");
      return navigate(`/meterstanden/ronde/${r.completedRoundId}?meter=${assetId}`);
    }
    toast("Er is geen open ronde voor dit gebouw. Maak er een op de pagina Meterstanden.");
  } catch (e) {
    toast(errMsg(e));
  }
}

export function MeterSection({
  asset,
  buildingId,
  buildingAssets,
}: {
  asset: AssetDto;
  buildingId: string;
  buildingAssets: AssetDto[];
}) {
  const { data: me } = useMe();
  const see = hasModule(me, "meters") && asset.assetKind === "meter";
  const canEdit = hasModule(me, "meters", "edit");
  const canFinding = hasModule(me, "issues", "edit");
  const qc = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [vacancy, setVacancy] = useState(false);
  const q = useAssetMeter(asset.id, see);
  if (!see) return null;
  const d = q.data;
  const m = d?.meter;
  async function finding(readingId: string) {
    try {
      const r = await api<{ findingId: string; code: string }>(`/meters/readings/${readingId}/finding`, {
        method: "POST",
        body: {},
      });
      toast(`Bevinding ${r.code} gemaakt`);
      void qc.invalidateQueries({ queryKey: assetMeterKey(asset.id) });
    } catch (e) {
      toast(errMsg(e));
    }
  }
  async function removeVacancy(id: string) {
    try {
      await api(`/meters/vacancies/${id}`, { method: "DELETE" });
      void qc.invalidateQueries({ queryKey: assetMeterKey(asset.id) });
    } catch (e) {
      toast(errMsg(e));
    }
  }
  async function endVacancy(id: string) {
    try {
      await api(`/meters/vacancies/${id}`, { method: "PATCH", body: { endsOn: todayIso() } });
      void qc.invalidateQueries({ queryKey: assetMeterKey(asset.id) });
      toast("Leegstand beëindigd");
    } catch (e) {
      toast(errMsg(e));
    }
  }

  return (
    <section className="sec metersec" aria-label="Meter">
      <div className="row">
        <p className="label grow">Meter</p>
        {d?.vacantNow && <span className="chip warn">Leeg</span>}
        {canEdit && (
          <button type="button" className="btn small" onClick={() => setEditing(true)}>
            {m ? "Instellingen" : "Meter instellen"}
          </button>
        )}
      </div>
      {!d ? (
        <Loading small />
      ) : !m ? (
        <p className="muted small">
          Nog niet ingesteld. Kies de soort meter en de telwerken, dan kan hij worden opgenomen.
        </p>
      ) : (
        <>
          <p className="small">
            {METER_TYPE_LABELS[m.meterType]}
            {m.unit && ` · ${m.unit}`}
            {m.registers.length > 1 && ` · ${m.registers.map((r) => r.name).join(", ")}`}
            {m.factor !== 1 && ` · factor ×${nl(m.factor)}`}
            {!m.active && " · niet actief"}
            {m.ean && <span className="block muted">EAN {m.ean}</span>}
            {m.meterNumber && <span className="block muted">Meternummer {m.meterNumber}</span>}
            {d.parent && (
              <span className="block">
                Tussenmeter van {d.parent.code} {d.parent.name}
              </span>
            )}
            {d.children.length > 0 && (
              <span className="block">
                Tussenmeters: {d.children.map((c) => `${c.code} ${c.name}`.trim()).join(", ")}
              </span>
            )}
          </p>

          {d.signals.length > 0 && (
            <ul className="list compact" aria-label="Signalen">
              {d.signals.map((s) => {
                const reading = d.readings.find((r) => r.id === s.readingId);
                return (
                  <li key={s.code} className="note warn signalrow">
                    <IconAlert size={18} aria-hidden="true" />
                    <span className="grow small">
                      <strong>{SIGNAL_LABELS[s.code]}</strong> · {s.message}
                    </span>
                    {reading?.findingId ? (
                      <Link className="small" to={`/gebouwen/${buildingId}?bevinding=${reading.findingId}`}>
                        {reading.findingCode ?? "Bevinding"}
                      </Link>
                    ) : (
                      canFinding && (
                        <button type="button" className="btn small" onClick={() => void finding(s.readingId)}>
                          Bevinding maken
                        </button>
                      )
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          <MeterChart periods={d.periods} unit={m.unit} />

          {d.vacancies.length > 0 && (
            <ul className="list compact" aria-label="Leegstand">
              {d.vacancies.map((v) => (
                <li key={v.id} className="row small">
                  <span className="grow">
                    Leeg {v.assetId ? "(deze meter)" : "(hele gebouw)"} vanaf {fmtDate(v.startsOn)}
                    {v.endsOn ? ` t/m ${fmtDate(v.endsOn)}` : ""}
                    {v.note && <span className="muted"> · {v.note}</span>}
                  </span>
                  {canEdit && v.assetId && !v.endsOn && (
                    <button type="button" className="btn small ghost" onClick={() => void endVacancy(v.id)}>
                      Beëindigen
                    </button>
                  )}
                  {canEdit && v.assetId && (
                    <button
                      type="button"
                      className="btn ico ghost small"
                      aria-label="Leegstand verwijderen"
                      onClick={() => void removeVacancy(v.id)}
                    >
                      <IconTrash size={16} aria-hidden="true" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {canEdit && (
            <button type="button" className="btn small ghost" onClick={() => setVacancy(true)}>
              <IconPlus size={16} aria-hidden="true" />
              Leegstand vastleggen
            </button>
          )}

          <p className="label">Opnames</p>
          {!d.readings.length ? (
            <p className="muted small">Nog geen opnames.</p>
          ) : (
            <ul className="list compact meterreadings" aria-label="Opnames">
              {d.readings.slice(0, 12).map((r) => {
                const p = d.periods.find((x) => x.readingId === r.id);
                return (
                  <li key={r.id} className="row small">
                    {r.photoFileId ? (
                      <a href={photoUrl(r.photoFileId)} target="_blank" rel="noopener" className="meterthumb">
                        <img src={thumbUrl(r.photoFileId)} alt={`Foto meter ${fmtDate(r.readOn)}`} />
                      </a>
                    ) : (
                      <span className="meterthumb empty" aria-hidden="true" />
                    )}
                    <span className="grow">
                      <strong>{fmtDate(r.readOn)}</strong>
                      {r.unreachable ? (
                        <span className="muted"> · niet bereikbaar: {r.unreachableReason}</span>
                      ) : (
                        <>
                          {" · "}
                          {m.registers
                            .map((reg) => {
                              const v = r.values.find((x) => x.registerId === reg.id);
                              return `${m.registers.length > 1 ? `${reg.name} ` : ""}${nl(v?.value)}`;
                            })
                            .join(" · ")}
                          {p?.consumption != null && (
                            <span className="muted">
                              {" "}
                              (+{nl(p.consumption, 1)} {m.unit} in {p.days} d)
                            </span>
                          )}
                        </>
                      )}
                      {r.event !== "normal" && <span className="chip small">{READING_EVENT_LABELS[r.event]}</span>}
                      {r.source === "import" && <span className="chip small">import</span>}
                      {r.remark && <span className="block muted">{r.remark}</span>}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
      {editing && d && (
        <MeterForm
          asset={asset}
          data={d}
          candidates={buildingAssets.filter((a) => a.assetKind === "meter" && a.id !== asset.id)}
          onClose={() => setEditing(false)}
          onSaved={(x) => {
            qc.setQueryData(assetMeterKey(asset.id), x);
            setEditing(false);
          }}
        />
      )}
      {vacancy && <VacancyForm asset={asset} buildingId={buildingId} onClose={() => setVacancy(false)} />}
    </section>
  );
}

type RegDraft = { id: string; name: string; direction: RegisterDirection };

/** Instellingen van een meter als concept: in het venster *Meter instellen* en bij een nieuwe asset. */
export type MeterDraft = {
  type: MeterType;
  unit: string;
  ean: string;
  number: string;
  digits: string;
  factor: string;
  parent: string;
  base: string;
  active: boolean;
  regs: RegDraft[];
};

export const meterDraft = (m: MeterDto | null): MeterDraft => ({
  type: m?.meterType ?? "electricity",
  unit: m?.unit ?? METER_DEFAULT_UNITS.electricity,
  ean: m?.ean ?? "",
  number: m?.meterNumber ?? "",
  digits: m?.digits?.toString() ?? "",
  factor: m ? nl(m.factor, 4) : "1",
  parent: m?.parentAssetId ?? "",
  base: m?.baseLoadPerDay != null ? nl(m.baseLoadPerDay) : "",
  active: m?.active ?? true,
  regs: m?.registers.map((r) => ({ id: r.id, name: r.name, direction: r.direction })) ?? [
    { id: crypto.randomUUID(), name: "Stand", direction: "consumption" },
  ],
});

/** Body voor PUT /api/assets/:id/meter. */
export const meterBody = (d: MeterDraft) => ({
  meterType: d.type,
  unit: d.unit.trim(),
  ean: d.ean.replace(/\s/g, "") || null,
  meterNumber: d.number.trim() || null,
  digits: d.digits ? Number(d.digits) : null,
  factor: parseNumber(d.factor) ?? 1,
  parentAssetId: d.parent || null,
  baseLoadPerDay: parseNumber(d.base),
  active: d.active,
  registers: d.regs.map((r) => ({ ...r, name: r.name.trim() })),
});

/** Velden van een meter: soort, eenheid, EAN, nummer, cijfers, factor, hoofdmeter, basislast, telwerken. */
export function MeterFields({
  draft,
  onChange,
  candidates,
  hasKids = false,
}: {
  draft: MeterDraft;
  onChange: (d: MeterDraft) => void;
  candidates: { id: string; code: string; name: string }[];
  hasKids?: boolean;
}) {
  const { type, unit, ean, number, digits, factor, parent, base, active, regs } = draft;
  const up = (v: Partial<MeterDraft>) => onChange({ ...draft, ...v });
  const setUnit = (unit: string) => up({ unit });
  const setEan = (ean: string) => up({ ean });
  const setNumber = (number: string) => up({ number });
  const setDigits = (digits: string) => up({ digits });
  const setFactor = (factor: string) => up({ factor });
  const setParent = (parent: string) => up({ parent });
  const setBase = (base: string) => up({ base });
  const setActive = (active: boolean) => up({ active });
  const setRegs = (regs: RegDraft[]) => up({ regs });
  const setReg = (i: number, v: Partial<RegDraft>) => setRegs(regs.map((r, k) => (k === i ? { ...r, ...v } : r)));
  return (
    <>
      <div className="grid2">
        <label className="field">
          <span>Soort meter</span>
          <select
            value={type}
            onChange={(e) => {
              const t = e.target.value as MeterType;
              up({ type: t, unit: !unit || unit === METER_DEFAULT_UNITS[type] ? METER_DEFAULT_UNITS[t] : unit });
            }}
          >
            {METER_TYPES.map((t) => (
              <option key={t} value={t}>
                {METER_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Eenheid</span>
          <input value={unit} onChange={(e) => setUnit(e.target.value)} list="meter-units" maxLength={20} />
          <datalist id="meter-units">
            {METER_UNIT_SUGGESTIONS.map((u) => (
              <option key={u} value={u} />
            ))}
          </datalist>
        </label>
        <label className="field">
          <span>EAN-code</span>
          <input value={ean} onChange={(e) => setEan(e.target.value)} inputMode="numeric" maxLength={22} />
        </label>
        <label className="field">
          <span>Meternummer</span>
          <input value={number} onChange={(e) => setNumber(e.target.value)} maxLength={60} />
        </label>
        <label className="field">
          <span>Cijfers vóór de komma</span>
          <input
            value={digits}
            onChange={(e) => setDigits(e.target.value.replace(/\D/g, ""))}
            inputMode="numeric"
            placeholder="bijv. 6"
          />
          <span className="hint">Nodig om rondtikken (99999 → 00000) te herkennen.</span>
        </label>
        <label className="field">
          <span>Vermenigvuldigingsfactor</span>
          <input value={factor} onChange={(e) => setFactor(e.target.value)} inputMode="decimal" />
        </label>
        <label className="field">
          <span>Hoofdmeter</span>
          <select value={parent} onChange={(e) => setParent(e.target.value)} disabled={hasKids}>
            <option value="">Geen (dit is een hoofdmeter)</option>
            {candidates.map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} {c.name}
              </option>
            ))}
          </select>
          {hasKids && <span className="hint">Deze meter heeft zelf tussenmeters.</span>}
        </label>
        <label className="field">
          <span>Basislast per dag{unit ? ` (${unit})` : ""}</span>
          <input value={base} onChange={(e) => setBase(e.target.value)} inputMode="decimal" placeholder="0" />
          <span className="hint">Verbruik bij leegstand dat geen signaal geeft, bijv. noodverlichting.</span>
        </label>
      </div>
      <fieldset className="stack">
        <legend className="label">Telwerken</legend>
        {regs.map((r, i) => (
          <div key={r.id} className="row">
            <input
              className="grow"
              aria-label={`Naam telwerk ${i + 1}`}
              value={r.name}
              onChange={(e) => setReg(i, { name: e.target.value })}
              maxLength={60}
              required
            />
            <select
              aria-label={`Richting telwerk ${i + 1}`}
              value={r.direction}
              onChange={(e) => setReg(i, { direction: e.target.value as RegisterDirection })}
            >
              {REGISTER_DIRECTIONS.map((x) => (
                <option key={x} value={x}>
                  {REGISTER_DIRECTION_LABELS[x]}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn ico ghost"
              aria-label={`Telwerk ${r.name || i + 1} verwijderen`}
              disabled={regs.length === 1}
              onClick={() => setRegs(regs.filter((_, k) => k !== i))}
            >
              <IconTrash size={18} aria-hidden="true" />
            </button>
          </div>
        ))}
        <div className="btnrow">
          <button
            type="button"
            className="btn small"
            onClick={() => setRegs([...regs, { id: crypto.randomUUID(), name: "", direction: "consumption" }])}
          >
            <IconPlus size={16} aria-hidden="true" />
            Telwerk
          </button>
          {type === "electricity" && regs.length === 1 && (
            <button
              type="button"
              className="btn small ghost"
              onClick={() =>
                setRegs([
                  { ...regs[0]!, name: "Normaal" },
                  { id: crypto.randomUUID(), name: "Dal", direction: "consumption" },
                ])
              }
            >
              Normaal + dal
            </button>
          )}
        </div>
      </fieldset>
      <label className="check">
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
        Actief (komt in nieuwe opnamerondes)
      </label>
    </>
  );
}

function MeterForm({
  asset,
  data,
  candidates,
  onClose,
  onSaved,
}: {
  asset: AssetDto;
  data: AssetMeterDto;
  candidates: AssetDto[];
  onClose: () => void;
  onSaved: (d: AssetMeterDto) => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [draft, setDraft] = useState(() => meterDraft(data.meter));
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api<AssetMeterDto>(`/assets/${asset.id}/meter`, { method: "PUT", body: meterBody(draft) });
      void qc.invalidateQueries({ queryKey: ["meter-buildings"] }); // telling in de lopende ronde
      void qc.invalidateQueries({ queryKey: ["building-meters"] });
      toast("Meter opgeslagen");
      onSaved(res);
    } catch (err) {
      toast(errMsg(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`${asset.code} ${asset.name}`}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Annuleren
          </button>
          <button type="submit" form="meterform" className="btn primary" disabled={busy}>
            Opslaan
          </button>
        </>
      }
    >
      <form id="meterform" className="stack" onSubmit={submit}>
        <MeterFields draft={draft} onChange={setDraft} candidates={candidates} hasKids={data.children.length > 0} />
      </form>
    </Modal>
  );
}

/** Leegstand van de unit achter deze meter (§3.3). Leegstand van het hele gebouw staat op de pagina Meterstanden. */
function VacancyForm({ asset, buildingId, onClose }: { asset: AssetDto; buildingId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [from, setFrom] = useState(todayIso());
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api(`/buildings/${buildingId}/meters/vacancies`, {
        method: "POST",
        body: { id: crypto.randomUUID(), assetId: asset.id, startsOn: from, endsOn: to || null, note: note || null },
      });
      void qc.invalidateQueries({ queryKey: assetMeterKey(asset.id) });
      toast("Leegstand vastgelegd");
      onClose();
    } catch (err) {
      toast(errMsg(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      title="Leegstand vastleggen"
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Annuleren
          </button>
          <button type="submit" form="vacancyform" className="btn primary" disabled={busy}>
            Opslaan
          </button>
        </>
      }
    >
      <form id="vacancyform" className="stack" onSubmit={submit}>
        <p className="small muted">
          Voor de unit achter {asset.code} {asset.name}. Verbruik boven de basislast geeft dan een signaal.
        </p>
        <div className="grid2">
          <label className="field">
            <span>Leeg vanaf</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} required />
          </label>
          <label className="field">
            <span>Tot en met (leeg = nog leeg)</span>
            <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
          </label>
        </div>
        <label className="field">
          <span>Opmerking</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} />
        </label>
      </form>
    </Modal>
  );
}
